import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { emailTemplate } from "@/lib/brain/email-templates";
import type { DeckCard } from "@/lib/brain/types";
import { useBrainRun } from "./useBrainRun";
import { ConfirmAction } from "./ConfirmAction";

// send-admin-email resolves the sender by name; this is what it sends as.
const FROM = "kontakt@coliviq.de";

/**
 * A prefilled plain-text email to the card's person, sent through
 * send-admin-email. `onSent` records it (brain_item_state.last_emailed_at).
 */
export function BrainEmailComposer({
  card,
  onSent,
  onDone,
}: {
  card: DeckCard;
  onSent(): Promise<void>;
  onDone(): void;
}) {
  const { t } = useLanguage();
  const { run, busy } = useBrainRun(onDone);
  // The template picks the first or the full name per kind.
  const name = card.refs.personName ?? "";
  const [lang, setLang] = useState<"de" | "en">("de");
  const ctx = { name, missing: card.missingFields };
  const initial = emailTemplate(card.kind, lang, ctx);
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [text, setText] = useState(initial?.text ?? "");
  const [dirty, setDirty] = useState(false);
  const to = card.refs.email?.trim();

  const switchLang = (l: "de" | "en") => {
    setLang(l);
    if (dirty) return;
    const tpl = emailTemplate(card.kind, l, ctx);
    setSubject(tpl?.subject ?? "");
    setText(tpl?.text ?? "");
  };

  if (!to) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("brain.email.noAddress", "No email address on file")}
      </p>
    );
  }

  const send = () =>
    run(async () => {
      const failed = t("brain.email.failed", "Email not sent");
      const { data, error } = await supabase.functions.invoke(
        "send-admin-email",
        { body: { to, subject, text, sender: "kontakt" } }
      );
      if (error) throw new Error(await edgeFunctionErrorMessage(error, failed));
      if (data?.success === false) throw new Error(data.error || failed);
      toast.success(t("brain.email.sent", "Email sent"));
      // The email is out: a failed stamp must not read as "not sent" and
      // invite a second send.
      await onSent().catch(() =>
        toast.warning(
          t(
            "brain.email.notRecorded",
            "Email sent, but it couldn't be recorded on the card"
          )
        )
      );
    });

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex flex-col">
          <span>
            {t("brain.email.from", "From")}: {FROM}
          </span>
          <span>
            {t("brain.email.to", "To")}: {to}
          </span>
        </span>
        <div className="flex gap-1">
          {(["de", "en"] as const).map((l) => (
            <Button
              key={l}
              size="sm"
              variant={lang === l ? "secondary" : "ghost"}
              aria-pressed={lang === l}
              onClick={() => switchLang(l)}
            >
              {l.toUpperCase()}
            </Button>
          ))}
        </div>
      </div>
      <Input
        value={subject}
        onChange={(e) => {
          setSubject(e.target.value);
          setDirty(true);
        }}
        aria-label={t("brain.email.subject", "Subject")}
      />
      <Textarea
        rows={6}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setDirty(true);
        }}
        aria-label={t("brain.email.body", "Message")}
      />
      <ConfirmAction
        title={t("brain.confirm.email.send", "Send this email?")}
        description={`${t("brain.email.from", "From")}: ${FROM}\n${t("brain.email.to", "To")}: ${to}\n${subject.trim()}`}
        confirmLabel={t("brain.email.send", "Send email")}
        onConfirm={() => void send()}
      >
        <Button size="sm" disabled={busy || !subject.trim() || !text.trim()}>
          {t("brain.email.send", "Send email")}
        </Button>
      </ConfirmAction>
    </div>
  );
}
